"use client";

import { IconButton, Menu, MenuItem } from "@mui/material";
import React, { useContext, useState } from "react";
import {
  MdCalendarMonth,
  MdMessage,
  MdMoreVert,
  MdPeople,
  MdEdit,
  MdComment,
  MdRedo,
  MdEditDocument,
  MdPersonAdd,
  MdAccountBalance,
  MdAttachFile,
  MdDescription,
} from "react-icons/md";
import { AuthContext } from "../../context/AuthContext";
import {
  ADMIN_PERMISSIONS,
  STUDENT_ONLY_PERMISSIONS,
  SUPER_ADMIN_PERMISSIONS,
  ACCOUNTANT_PERMISSIONS,
} from "../../data/roles";
import { UserRolesEnum } from "../../typings";
import {
  APPLICATION_STATUS,
  ScholarshipApplicationsListingProps,
} from "../../typings/scholarship-applications";
import ScheduleInterviewModal from "./modals/ScheduleInterviewModal";
import DonorsModal from "./modals/donors-modal";
import FeedbackModal from "./modals/feedback-modal";
import CommentModal from "./modals/comment-modal";
import AdjustApplicationModal from "./modals/adjust-application-modal";
import AssignMentorModal from "./modals/assign-mentor-modal";
import MentorFeedbackModal from "./modals/mentor-feedback-modal";
import CreateTransactionModal from "../transactions/CreateTransactionModal";
import UploadExtraDocumentsModal from "./modals/upload-extra-documents-modal";
import SignContractModal from "./modals/sign-contract-modal";
import { TRANSACTION_INITIAL_VALUES } from "../../data/transactions";

type ScholarshipActionMenuProps = {
  application: ScholarshipApplicationsListingProps;
  onRefresh: () => void;
};

export const ScholarshipActionMenu: React.FC<ScholarshipActionMenuProps> = ({
  application,
  onRefresh,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [donorsModalOpen, setDonorsModalOpen] = useState(false);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [adjustApplicationModal, setAdjustApplicationModal] = useState(false);
  const [assignMentorModalOpen, setAssignMentorModalOpen] = useState(false);
  const [mentorFeedbackModalOpen, setMentorFeedbackModalOpen] = useState(false);
  const [createTransactionModalOpen, setCreateTransactionModalOpen] = useState(false);
  const [uploadExtraDocumentsModalOpen, setUploadExtraDocumentsModalOpen] = useState(false);
  const [signContractModalOpen, setSignContractModalOpen] = useState(false);
  const [viewContractModalOpen, setViewContractModalOpen] = useState(false);

  const auth = useContext(AuthContext);
  const isAdmin = [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  const isAccountant = [...ACCOUNTANT_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  const isStudent = [...STUDENT_ONLY_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenInterviewModal = () => {
    setInterviewModalOpen(true);
    handleClose();
  };

  const handleOpenCommentsModal = () => {
    setCommentsModalOpen(true);
    handleClose();
  };

  const handleOpenAdjustApplicationModal = () => {
    setAdjustApplicationModal(true);
    handleClose();
  };

  const handleEditApplication = (id: number) => {
    window.open(`/scholarship-form/${id}`, "_blank", "noopener,noreferrer");
  };

  const handleOpenFeedbackModal = () => {
    setFeedbackModalOpen(true);
    handleClose();
  };

  const handleOpenDonorsModal = () => {
    setDonorsModalOpen(true);
    handleClose();
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  const handleOpenAssignMentorModal = () => {
    setAssignMentorModalOpen(true);
    handleClose();
  };

  const handleOpenMentorFeedbackModal = () => {
    setMentorFeedbackModalOpen(true);
    handleClose();
  };

  const handleReapplyApplication = () => {
    window.open(
      `/scholarship-reapply/${application.id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenCreateTransactionModal = () => {
    setCreateTransactionModalOpen(true);
    handleClose();
  };

  const handleCloseCreateTransactionModal = () => {
    setCreateTransactionModalOpen(false);
  };

  const handleOpenUploadExtraDocumentsModal = () => {
    setUploadExtraDocumentsModalOpen(true);
    handleClose();
  };

  const handleOpenSignContractModal = () => {
    setSignContractModalOpen(true);
    handleClose();
  };

  const handleOpenViewContractModal = () => {
    setViewContractModalOpen(true);
    handleClose();
  };

  const transactionInitialValues = {
    ...TRANSACTION_INITIAL_VALUES,
    scholarship_application_id: application.id,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
      }}
    >
      {/* Actions Menu */}
      <IconButton
        aria-label="Application Actions"
        aria-controls={open ? "application-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
        style={{ height: "32px", width: "32px" }}
      >
        <MdMoreVert style={{ height: "16px", width: "16px" }} />
      </IconButton>
      <Menu
        id="application-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "actions-button",
          style: { width: "224px" },
        }}
        PaperProps={{
          style: {
            borderRadius: "6px",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        {isAdmin && (
          <MenuItem
            onClick={handleOpenInterviewModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={
              !!application.parent_application ||
              ![APPLICATION_STATUS.IN_PROCESS].includes(
                application.status as APPLICATION_STATUS
              )
            }
          >
            <MdCalendarMonth
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Schedule Interview
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenAssignMentorModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={
              !application.parent_application ||
              ![APPLICATION_STATUS.IN_PROCESS].includes(
                application.status as APPLICATION_STATUS
              )
            }
          >
            <MdPersonAdd
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Assign Mentor
          </MenuItem>
        )}
        {auth?.role === UserRolesEnum.INTERVIEWER && (
          <MenuItem
            onClick={handleOpenFeedbackModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={
              ![
                APPLICATION_STATUS.IN_PROCESS,
                APPLICATION_STATUS.APPROVED,
              ].includes(application.status as APPLICATION_STATUS)
            }
          >
            <MdMessage
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Interview Feedback
          </MenuItem>
        )}
        {auth?.role === UserRolesEnum.MENTOR && (
          <MenuItem
            onClick={handleOpenMentorFeedbackModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={
              ![
                APPLICATION_STATUS.IN_PROCESS,
                APPLICATION_STATUS.APPROVED,
              ].includes(application.status as APPLICATION_STATUS)
            }
          >
            <MdMessage
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Mentor Feedback
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenDonorsModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={[
              APPLICATION_STATUS.IN_PROCESS,
              APPLICATION_STATUS.SUBMITTED,
              APPLICATION_STATUS.REJECTED,
            ].includes(application.status as APPLICATION_STATUS)}
          >
            <MdPeople
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Tag Donors
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenCommentsModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdComment
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Add comment
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenAdjustApplicationModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdEditDocument
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Adjust Application
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={handleOpenUploadExtraDocumentsModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdAttachFile
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Upload Additional Docs
          </MenuItem>
        )}
        {isStudent && !application.parent_application && (
          <MenuItem
            onClick={handleReapplyApplication}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdRedo
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Reapply
          </MenuItem>
        )}
        {isStudent && (
          <MenuItem
            onClick={() => handleEditApplication(application.id)}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
            disabled={!application.is_resubmission_allowed}
          >
            <MdEdit
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Edit Application
          </MenuItem>
        )}
        {isStudent &&
          application.contract_signed === true &&
          application.generated_contract && (
            <MenuItem
              onClick={handleOpenViewContractModal}
              style={{
                fontSize: "14px",
                padding: "8px 12px",
              }}
            >
              <MdDescription
                style={{ marginRight: "8px", height: "16px", width: "16px" }}
              />
              View Contract
            </MenuItem>
          )}
        {isStudent &&
          application.contract_signed !== true &&
          application.generated_contract && (
            <MenuItem
              onClick={handleOpenSignContractModal}
              style={{
                fontSize: "14px",
                padding: "8px 12px",
              }}
            >
              <MdDescription
                style={{ marginRight: "8px", height: "16px", width: "16px" }}
              />
              Sign Contract
            </MenuItem>
          )}
        {isAdmin && application.generated_contract && (
          <MenuItem
            onClick={handleOpenViewContractModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdDescription
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            View Contract
          </MenuItem>
        )}
        {(isAdmin || isAccountant) && (
          <MenuItem
            onClick={handleOpenCreateTransactionModal}
            style={{
              fontSize: "14px",
              padding: "8px 12px",
            }}
          >
            <MdAccountBalance
              style={{ marginRight: "8px", height: "16px", width: "16px" }}
            />
            Make Transaction
          </MenuItem>
        )}
      </Menu>

      {/* Modals */}

      <ScheduleInterviewModal
        open={interviewModalOpen}
        onClose={() => setInterviewModalOpen(false)}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        applicationId={application.id}
        onRefresh={onRefresh}
      />
      <AssignMentorModal
        open={assignMentorModalOpen}
        onClose={() => setAssignMentorModalOpen(false)}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      <MentorFeedbackModal
        open={mentorFeedbackModalOpen}
        onClose={() => setMentorFeedbackModalOpen(false)}
        applicationId={application.id}
        onRefresh={onRefresh}
      />
      <DonorsModal
        open={donorsModalOpen}
        onClose={() => setDonorsModalOpen(false)}
        donorsTagged={application.donors_tagged}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      <CommentModal
        open={commentsModalOpen}
        onClose={() => setCommentsModalOpen(false)}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      <AdjustApplicationModal
        open={adjustApplicationModal}
        onClose={() => setAdjustApplicationModal(false)}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      <CreateTransactionModal
        open={createTransactionModalOpen}
        onClose={handleCloseCreateTransactionModal}
        transaction={transactionInitialValues}
        onSuccess={() => {
          handleCloseCreateTransactionModal();
          onRefresh();
        }}
        hideScholarshipSelection={true}
        scholarshipApplicationName={application.name}
      />
      <UploadExtraDocumentsModal
        open={uploadExtraDocumentsModalOpen}
        onClose={() => setUploadExtraDocumentsModalOpen(false)}
        scholarshipApplicationId={application.id}
        onRefresh={onRefresh}
      />
      {application.generated_contract && (
        <SignContractModal
          open={signContractModalOpen}
          onClose={() => setSignContractModalOpen(false)}
          scholarshipApplicationId={application.id}
          contractUrl={application.generated_contract}
          onRefresh={onRefresh}
        />
      )}
      {application.generated_contract && (
        <SignContractModal
          open={viewContractModalOpen}
          onClose={() => setViewContractModalOpen(false)}
          scholarshipApplicationId={application.id}
          contractUrl={application.generated_contract}
          onRefresh={onRefresh}
          viewOnly
        />
      )}
    </div>
  );
};
